import { useEffect, useState } from "react";
import { useAccount, useContractRead } from "wagmi";
import { Chains, SpaceHigherAddrABI, SpaceInviteCodeABI } from "../data";
import { useAppSelector } from "../../app/hooks";
import { selectChain } from "../../features/chain";
import MyInvitation from "./myInvitation";
import ConfirmBtn from "./confirmBtn";
import { emptyEthAddress } from "../../utils/util";
import { getJicAddress, onJicChange } from "../../lib/jic";
import { resolveJicReferralInput } from "../../lib/jicReferral";
import { getInjectedWalletAddress, hasInjectedProvider } from "../../lib/injectedWallet";

export type CardProps = {
  renderImg: boolean;
};

const InvitationCode = () => {
  const chain = useAppSelector(selectChain);
  const { address: signerAddress } = useAccount();
  const [jicAddress, setJicAddress] = useState<string | null>(() => getJicAddress());
  const [injectedAddress, setInjectedAddress] = useState<string | null>(null);
  const address =
    jicAddress ||
    (signerAddress ? signerAddress.toLowerCase() : null) ||
    injectedAddress;
  const [showMyInvitation, setShowMyInvitation] = useState<boolean | null>(
    null
  );

  const { refetch: fetchHigherAddr } = useContractRead({
    chainId: Chains[chain].chainId,
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceHigherAddrABI,
    functionName: "higherAddress",
    args: [(address ?? emptyEthAddress) as `0x${string}`],
    enabled: false,
  });
  const { refetch: fetchInviteCode } = useContractRead({
    chainId: Chains[chain].chainId,
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceInviteCodeABI,
    functionName: "inviteCode",
    args: [(address ?? emptyEthAddress) as `0x${string}`],
    enabled: false,
  });

  useEffect(
    () =>
      onJicChange((user) => {
        setJicAddress(user?.sub ? user.sub.toLowerCase() : null);
      }),
    []
  );

  useEffect(() => {
    let live = true;
    const refreshInjectedAddress = async () => {
      if (!hasInjectedProvider()) {
        if (live) setInjectedAddress(null);
        return;
      }
      const walletAddress = await getInjectedWalletAddress({
        requestIfNeeded: false,
      }).catch(() => null);
      if (live) setInjectedAddress(walletAddress ? walletAddress.toLowerCase() : null);
    };
    refreshInjectedAddress();
    window.addEventListener("focus", refreshInjectedAddress);
    return () => {
      live = false;
      window.removeEventListener("focus", refreshInjectedAddress);
    };
  }, []);

  const refreshHigherAddr = async () => {
    if (!address) {
      setShowMyInvitation(null);
      return;
    }
    try {
      const [higherAddrResult, inviteCodeResult] = await Promise.allSettled([
        fetchHigherAddr(),
        fetchInviteCode(),
      ]);
      const higherAddrRes =
        higherAddrResult.status === "fulfilled"
          ? (higherAddrResult.value.data as string)
          : "";
      const inviteCodeRes =
        inviteCodeResult.status === "fulfilled"
          ? (inviteCodeResult.value.data as string)
          : "";
      const hasHigherAddr =
        higherAddrRes &&
        higherAddrRes !== "" &&
        higherAddrRes !== "0x0000000000000000000000000000000000000000";
      const hasInviteCode =
        typeof inviteCodeRes === "string" && inviteCodeRes.trim() !== "";
      const fallbackCode =
        hasHigherAddr || hasInviteCode
          ? ""
          : await resolveJicReferralInput("face", address);
      setShowMyInvitation(
        Boolean(hasHigherAddr || hasInviteCode || fallbackCode)
      );
    } catch {
      setShowMyInvitation(true);
    }
  };

  useEffect(() => {
    if (address) {
      setShowMyInvitation(null);
      refreshHigherAddr();
    } else {
      setShowMyInvitation(null);
    }
  }, [address, chain]);

  return (
    <div className="w-full flex justify-center">
      {address && showMyInvitation !== false ? (
        <MyInvitation />
      ) : (
        <ConfirmBtn refreshHigherAddr={refreshHigherAddr} />
      )}
    </div>
  );
};
export default InvitationCode;
